import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Inbox, Plus, Search, UserPlus } from 'lucide-react';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { cn } from '../../lib/cn';
import { useResellers, usePendingApplicationsCount, addReseller } from '../../api/admin';

const STATUS_TONE = {
  active: 'success' as const,
  invited: 'info' as const,
  frozen: 'warning' as const,
  terminated: 'error' as const,
};

const STATUS_LABEL = {
  active: 'Active',
  invited: 'Invited',
  frozen: 'Frozen',
  terminated: 'Terminated',
};

/** Resellers list — table + status filter + invite modal. */
export function Resellers() {
  const { data: resellers = [], isLoading } = useResellers();
  const { data: pendingApplicationsCount = 0 } = usePendingApplicationsCount();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'invited' | 'frozen'>('all');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const filtered = resellers.filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (search && !r.name.toLowerCase().includes(search.toLowerCase()) && !r.slug.includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto">
      <PageHeader
        eyebrow="Platform"
        title={<span>Resellers {isLoading ? <Skeleton className="inline-block w-12 h-5 rounded align-middle" /> : <span className="text-zinc-400 dark:text-zinc-500 font-medium tabular-nums">({resellers.length})</span>}</span>}
        subtitle="Partners selling Possignin under their own brand."
        action={
          <Button iconLeft={<UserPlus className="w-4 h-4" />} onClick={() => setInviteOpen(true)}>
            Invite reseller
          </Button>
        }
      />

      {/* Active vs Pending tab strip — Pending links out to the applications queue. */}
      <div
        role="tablist"
        className="flex gap-6 border-b border-zinc-200 dark:border-zinc-700 mb-5 -mx-1 px-1 overflow-x-auto nice-scroll"
      >
        <button
          type="button"
          role="tab"
          aria-selected="true"
          className="py-3 -mb-px border-b-2 text-sm font-medium transition flex items-center gap-2 whitespace-nowrap border-brand text-zinc-950 dark:border-brand-dm dark:text-zinc-50"
        >
          Active resellers
          <span className="px-1.5 py-px rounded text-[10px] font-medium tabular-nums bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm">
            {resellers.length}
          </span>
        </button>
        <Link
          to="/admin/resellers/applications"
          role="tab"
          aria-selected="false"
          className="py-3 -mb-px border-b-2 text-sm font-medium transition flex items-center gap-2 whitespace-nowrap border-transparent text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:border-zinc-300 dark:hover:border-zinc-700"
        >
          <Inbox className="w-3.5 h-3.5" />
          Pending applications
          <span
            className={cn(
              'px-1.5 py-px rounded text-[10px] font-medium tabular-nums',
              pendingApplicationsCount > 0
                ? 'bg-rose-600 text-white'
                : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
            )}
          >
            {pendingApplicationsCount}
          </span>
        </Link>
      </div>

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <FilterDropdown
            label="Status"
            value={statusFilter === 'all' ? 'All' : STATUS_LABEL[statusFilter]}
            options={[
              { v: 'all', l: 'All' },
              { v: 'active', l: 'Active' },
              { v: 'invited', l: 'Invited' },
              { v: 'frozen', l: 'Frozen' },
            ]}
            onChange={(v) => setStatusFilter(v as typeof statusFilter)}
          />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search by name or slug…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Name</th>
                <th className="text-left font-medium px-4 py-3">Slug</th>
                <th className="text-right font-medium px-4 py-3">Businesses</th>
                <th className="text-right font-medium px-4 py-3">MRR</th>
                <th className="text-right font-medium px-4 py-3 hidden md:table-cell">Commission</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <Skeleton className="w-8 h-8 rounded-md flex-shrink-0" />
                        <div>
                          <Skeleton className="h-4 w-40 max-w-full rounded mb-1.5" />
                          <Skeleton className="h-3 w-28 max-w-full rounded" />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4"><Skeleton className="h-4 w-32 rounded" /></td>
                    <td className="px-4 py-4"><Skeleton className="h-4 w-10 rounded ml-auto" /></td>
                    <td className="px-4 py-4"><Skeleton className="h-4 w-20 rounded ml-auto" /></td>
                    <td className="px-4 py-4 hidden md:table-cell"><Skeleton className="h-4 w-12 rounded ml-auto" /></td>
                    <td className="px-4 py-4"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-5 sm:px-6 py-4"></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No resellers found.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                <tr
                  key={r.id}
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate({ to: '/admin/resellers/$id', params: { id: r.id } })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate({ to: '/admin/resellers/$id', params: { id: r.id } });
                    }
                  }}
                  className="group cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/70 focus:bg-zinc-50 dark:focus:bg-zinc-800/70 focus:outline-none transition"
                >
                  <td className="px-5 sm:px-6 py-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <span
                        className="w-8 h-8 rounded-md flex-shrink-0 grid place-items-center text-[10px] font-semibold text-white"
                        style={{ background: r.primaryColor }}
                      >
                        {r.name.split(' ').slice(0, 2).map((p) => p.charAt(0)).join('')}
                      </span>
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-950 dark:text-zinc-50 group-hover:text-brand dark:group-hover:text-brand-dm transition truncate">
                          {r.name}
                        </div>
                        <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{r.ownerEmail}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 mono text-xs text-zinc-600 dark:text-zinc-300">{r.slug}.itcitysoft.com</td>
                  <td className="px-4 py-4 text-right tabular-nums text-zinc-700 dark:text-zinc-200">{r.businessCount}</td>
                  <td className="px-4 py-4 text-right">
                    <Money minor={r.mrrMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50" />
                  </td>
                  <td className="px-4 py-4 text-right tabular-nums text-zinc-600 dark:text-zinc-300 hidden md:table-cell">{r.commissionPct}%</td>
                  <td className="px-4 py-4">
                    <Badge tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Badge>
                  </td>
                  <td className="px-5 sm:px-6 py-4 text-right">
                    <ChevronRight className="w-4 h-4 text-zinc-400 inline-block" aria-hidden="true" />
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400 flex items-center justify-between">
          <span>Showing <span className="tabular-nums">{filtered.length}</span> of <span className="tabular-nums">{resellers.length}</span></span>
          <span className="hidden sm:inline">Resellers earn commission on every business they bring in.</span>
        </div>
      </Card>

      <InviteResellerModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={() => void queryClient.invalidateQueries({ queryKey: ['admin', 'resellers'] })}
      />
    </div>
  );
}

function FilterDropdown({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ v: string; l: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-10 min-w-[160px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1">
          {options.map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => {
                onChange(o.v);
                setOpen(false);
              }}
              className={cn(
                'block w-full text-left px-3 py-1.5 text-xs text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800',
              )}
            >
              {o.l}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function InviteResellerModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [email, setEmail] = useState('');
  const [owner, setOwner] = useState('');
  const [commission, setCommission] = useState('20');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ inviteUrl: string; email: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const slugValid = /^[a-z0-9][a-z0-9-]{1,39}$/.test(slug);
  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const pctNum = Number(commission);
  const pctValid = Number.isInteger(pctNum) && pctNum >= 0 && pctNum <= 100;
  const valid = name.trim().length >= 2 && slugValid && emailValid && pctValid;

  async function submit() {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await addReseller({
        name: name.trim(),
        slug: slug.trim(),
        ownerEmail: email.trim(),
        ownerName: owner.trim() || undefined,
        commissionPct: pctNum,
      });
      setDone({ inviteUrl: res.inviteUrl, email: res.email });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the reseller. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setName(''); setSlug(''); setEmail(''); setOwner(''); setCommission('20');
    setError(null); setDone(null); setCopied(false);
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={reset}
      title="Invite a new reseller"
      size="lg"
      footer={
        done ? (
          <Button onClick={reset}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={reset} disabled={submitting}>Cancel</Button>
            <Button onClick={submit} loading={submitting} disabled={!valid} iconRight={<Plus className="w-4 h-4" />}>
              Create &amp; invite
            </Button>
          </>
        )
      }
    >
      {done ? (
        <div className="space-y-4">
          <Banner tone="success" title="Reseller created">
            An invitation email is on its way to <span className="mono text-xs">{done.email}</span>. Accepting it
            creates their login and reseller-admin access.
          </Banner>
          <div>
            <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Invite link (share manually if needed)</label>
            <div className="flex gap-2">
              <input
                readOnly
                value={done.inviteUrl}
                className="flex-1 px-3 py-2.5 text-xs mono rounded-md bg-zinc-50 dark:bg-zinc-950 border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200"
                onFocus={(e) => e.target.select()}
              />
              <Button
                variant="secondary"
                onClick={() => {
                  void navigator.clipboard.writeText(done.inviteUrl).then(() => setCopied(true));
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <Input label="Org name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Reseller organization" />
          <div>
            <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
              URL slug <span className="text-rose-600">*</span>
            </label>
            <div className="flex">
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder="reseller-slug"
                className="flex-1 px-3 py-2.5 text-sm rounded-l-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 border-r-0 focus:outline-none focus:ring-2 focus:ring-brand mono"
              />
              <span className="px-3 py-2.5 text-sm bg-zinc-50 dark:bg-zinc-950 border border-zinc-300 dark:border-zinc-700 rounded-r-md text-zinc-500 dark:text-zinc-400 mono">
                .itcitysoft.com
              </span>
            </div>
            {slug && !slugValid && (
              <span className="text-xs text-rose-600 dark:text-rose-400 mt-1.5 inline-block">
                2–40 chars: lowercase letters, digits and dashes.
              </span>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Input label="Owner email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
            <Input label="Owner name" value={owner} onChange={(e) => setOwner(e.target.value)} hint="Optional" />
          </div>
          <Input
            label="Commission %"
            hint="Platform cut of this reseller's collected payments (0–100)"
            value={commission}
            onChange={(e) => setCommission(e.target.value)}
            suffix={<span className="text-xs">%</span>}
            error={commission && !pctValid ? 'Whole number between 0 and 100' : undefined}
          />
          {error && <Banner tone="error" title="Couldn’t create the reseller">{error}</Banner>}
        </div>
      )}
    </Modal>
  );
}

function Radio({
  name,
  checked,
  onChange,
  label,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        className="text-brand focus:ring-brand"
      />
      {label}
    </label>
  );
}
